import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contextStore/AuthContext';
import { ToastContext } from '../../contextStore/ToastContext';
import { getCategoryFields } from '../../data/categoryFields';
import { getProductRef } from '../../firebase/collections';
import { Firebase } from '../../firebase/config';
import { notifyPriceDrop } from '../../firebase/notifications';
import { recordPriceHistory } from '../../firebase/priceHistory';
import {
  validateAdDescription,
  validateAdTitle,
  validatePrice,
} from '../../utils/validation';
import { isRewardCategory, TOKEN_KINDS } from '../../utils/tokenConfig';
import OverlaySpinner from '../Loading/OverlaySpinner';
import AdForm from './AdForm';
import './Create.css';

const initialValues = {
  name: '',
  category: '',
  subcategory: '',
  price: '',
  description: '',
  images: [],
  condition: '',
  negotiable: false,
  listedBy: '',
  location: {},
  extra: {},
};

function EditAd() {
  const { t, i18n } = useTranslation();
  const { adId } = useParams();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { addToast } = useContext(ToastContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  const initialPriceRef = React.useRef(null);

  const onChange = useCallback((field, value) => {
    setValues((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'category') {
        next.subcategory = '';
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev, [field]: null };
      // Location: only clear error when both state and city are filled
      if (field === 'location' && value && typeof value === 'object') {
        if (!value.state || !value.city) {
          next.location = prev.location;
        }
      }
      // When 'extra' changes, clear extra.* errors for filled keys
      if (field === 'extra' && value && typeof value === 'object') {
        Object.entries(value).forEach(([k, v]) => {
          if (v !== '' && v != null) {
            next[`extra.${k}`] = null;
          }
        });
      }
      // When category changes, clear all extra.* errors
      if (field === 'category') {
        Object.keys(prev).forEach((k) => {
          if (k.startsWith('extra.')) next[k] = null;
        });
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!adId || !user) return;
    getProductRef(adId)
      .get()
      .then((doc) => {
        if (!doc.exists) {
          setError(t('createAd.adNotFound'));
          setLoading(false);
          return;
        }
        const data = doc.data();
        if (data.userId !== user.uid) {
          setError(t('createAd.onlyEditOwnAds'));
          setLoading(false);
          return;
        }
        const priceVal =
          data.price !== undefined && data.price !== null ? data.price : '';
        initialPriceRef.current =
          typeof priceVal === 'number' ? priceVal : Number(priceVal) || null;
        setValues({
          name: data.name || '',
          category: data.category || '',
          subcategory: data.subcategory || '',
          price:
            typeof priceVal === 'number' || typeof priceVal === 'string'
              ? String(priceVal)
              : '',
          description: data.description || '',
          condition: data.condition || '',
          negotiable: Boolean(data.negotiable),
          listedBy: data.listedBy || '',
          location: data.location || {},
          extra: data.extra || {},
          images:
            data.images && data.images.length
              ? data.images
              : data.url
                ? [data.url]
                : [],
          // Load every field the form renders so users see (and can edit)
          // their actual ad data instead of defaults.
          adType: data.adType || 'for_sale',
          priceType: data.priceType || 'fixed',
          tags: Array.isArray(data.tags) ? data.tags : [],
          videoUrl: data.videoUrl || '',
          delivery: data.delivery || {},
          warranty: data.warranty || {},
          returnPolicy: data.returnPolicy || 'no_returns',
          paymentMethods: Array.isArray(data.paymentMethods)
            ? data.paymentMethods
            : [],
          contactPreference: data.contactPreference || 'both',
          availableFrom: data.availableFrom || '',
        });
        setLoading(false);
      })
      .catch(() => {
        setError(t('createAd.failedToLoadAd'));
        setLoading(false);
      });
  }, [adId, user]);

  const scrollToFirstError = (errs) => {
    setTimeout(() => {
      const keys = Object.keys(errs);
      if (!keys.length) return;
      for (const key of keys) {
        const clean = key.replace('extra.', '');
        if (clean === 'location') {
          const stateEl = document.getElementById('ad-location-state');
          const cityEl = document.getElementById('ad-location-city');
          const target = stateEl && !stateEl.value ? stateEl : cityEl;
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            target.focus({ preventScroll: true });
            return;
          }
        }
        const el = document.getElementById(`ad-${clean}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus({ preventScroll: true });
          return;
        }
      }
      const errEl = document.querySelector('.cf-error');
      if (errEl) errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleSubmit = () => {
    const err = {};

    if (!values.category?.trim()) err.category = t('createAd.pleaseSelectCategory');
    const tErr = validateAdTitle(values.name);
    if (tErr) err.name = tErr;
    const dErr = validateAdDescription(values.description);
    if (dErr) err.description = dErr;
    const pErr = validatePrice(values.price);
    if (pErr) err.price = pErr;
    if (!values.condition?.trim()) err.condition = t('createAd.pleaseSelectCondition');

    const existingUrls = values.images.filter((i) => typeof i === 'string');
    const newFiles = values.images.filter((i) => i instanceof File);
    if (existingUrls.length === 0 && newFiles.length === 0)
      err.images = t('createAd.pleaseAddImage');

    if (!values.location?.state || !values.location?.city)
      err.location = t('createAd.pleaseSelectLocation');

    // Validate category-specific required fields
    const catFields = getCategoryFields(values.category);
    const extra = values.extra || {};
    catFields.forEach((f) => {
      if (f.required) {
        const val = extra[f.key];
        if (val === undefined || val === null || String(val).trim() === '') {
          const fieldLabel = t(`adFields.${f.key}`, {
            defaultValue: f.label.replace(/\s*\*/, ''),
          });
          err[`extra.${f.key}`] = t('createAd.isRequired', { field: fieldLabel });
        }
      }
    });

    setErrors(err);
    if (Object.keys(err).length > 0) {
      const count = Object.keys(err).length;
      addToast(
        t('createAd.fixErrors', { count: count }),
        'error'
      );
      scrollToFirstError(err);
      return;
    }

    setSaving(true);
    const uploadNew = newFiles.map((file) =>
      Firebase.storage()
        .ref(`/image/${user.uid}_${Date.now()}_${file.name}`)
        .put(file)
        .then(({ ref }) => ref.getDownloadURL())
    );
    Promise.all(uploadNew)
      .then((newUrls) => {
        const allUrls = [...existingUrls, ...newUrls];
        const newPrice = Number(values.price);
        // Seller edits the canonical text in their current UI language. The text may
        // have changed, so invalidate any previously cached translations.
        const updateData = {
          name: values.name,
          category: values.category,
          subcategory: values.subcategory || null,
          price: newPrice,
          // "Bonuses" items are priced in the reward token, everything else in CXBT
          paymentToken: isRewardCategory(values.category)
            ? TOKEN_KINDS.REWARD
            : TOKEN_KINDS.STANDARD,
          description: values.description,
          originalLanguage: i18n.language || 'en',
          translations: {},
          url: allUrls[0],
          images: allUrls,
          condition: values.condition || null,
          negotiable: Boolean(values.negotiable),
          listedBy: values.listedBy || null,
          // Persist every field the form renders (same normalization as
          // buildProductData in createHandlers.js, so an edited ad keeps the
          // exact document shape a newly created ad has).
          adType: values.adType || 'for_sale',
          priceType: values.priceType || 'fixed',
          tags: Array.isArray(values.tags) ? values.tags : [],
          videoUrl: values.videoUrl || '',
          delivery:
            values.delivery && typeof values.delivery === 'object'
              ? {
                  ...values.delivery,
                  shippingCost:
                    values.delivery.shippingCost === '' ||
                    values.delivery.shippingCost == null
                      ? 0
                      : Number(values.delivery.shippingCost) || 0,
                }
              : {},
          warranty:
            values.warranty && typeof values.warranty === 'object'
              ? values.warranty
              : {},
          returnPolicy: values.returnPolicy || 'no_returns',
          paymentMethods: Array.isArray(values.paymentMethods)
            ? values.paymentMethods
            : [],
          contactPreference: values.contactPreference || 'both',
          availableFrom: values.availableFrom || null,
          location:
            values.location && (values.location.city || values.location.state)
              ? values.location
              : null,
          updatedAt: new Date().toISOString(),
        };
        if (values.extra && Object.keys(values.extra).length > 0) {
          const extraClean = {};
          Object.entries(values.extra).forEach(([k, v]) => {
            if (v !== '' && v != null) extraClean[k] = v;
          });
          updateData.extra =
            Object.keys(extraClean).length > 0 ? extraClean : null;
        }
        return getProductRef(adId)
          .update(updateData)
          .then(() => {
            if (
              initialPriceRef.current !== null &&
              newPrice !== initialPriceRef.current
            ) {
              return recordPriceHistory(adId, newPrice).then(() =>
                notifyPriceDrop(adId, values.price, values.name)
              );
            }
            return notifyPriceDrop(adId, values.price, values.name);
          });
      })
      .then(() => history.push('/dashboard'))
      .catch(() => {
        setSaving(false);
        addToast(t('createAd.failedToPostAd'), 'error');
      })
      .finally(() => setSaving(false));
  };

  if (!user) {
    return (
      <div className="profilePage">
        <p>{t('createAd.pleaseLogInToEditAds')}</p>
      </div>
    );
  }

  if (loading) {
    return <OverlaySpinner />;
  }

  if (error) {
    return (
      <div className="profilePage">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <Fragment>
      {saving && <OverlaySpinner />}
      <div className="createPageWrap">
        <div className="createPageActions">
          <Link to="/dashboard" className="createCancelLink">
            ← {t('common.back')} {t('common.backToDashboard')}
          </Link>
        </div>
        <AdForm
          values={values}
          onChange={onChange}
          errors={errors}
          loading={saving}
          onSubmit={handleSubmit}
          submitLabel={t('createAd.submit')}
        />
      </div>
    </Fragment>
  );
}

export default EditAd;
